import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { API, apiClient as axios } from "../../lib/api";
import { Toast, Pagination } from "../../components/ui";
import LeadDetailsModal from "../../components/LeadDetailsModal";
import Toolbar from "./components/Toolbar";
import LeadsTable from "./components/LeadsTable";
import AddLeadModal from "./components/AddLeadModal";
import { EMAIL_REGEX } from "./constants";

export default function LeadMakerDashboard({ token, user }) {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [segment, setSegment] = useState("");
  const [customer, setCustomer] = useState({ name: "", phone_number: "", email: "", delivery_address: "", area_code: "", city: "", district: "", state: "" });
  const [products, setProducts] = useState([{ product_id: "", product_name: "", size_variant: "", price: 0, quantity: 1 }]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [err, setErr] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const now = new Date();
  const indianTimeDate = new Date(now.getTime() + 19800000).toISOString();
  const today = useMemo(() => indianTimeDate.split('T')[0], []);
  const [deliveryDate, setDeliveryDate] = useState(today);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const canCreateLead = useMemo(() => {
    if (user?.status === 'blocked') return false;
    if (user?.status === 'pending' && leads.length > 0) return false;
    return true;
  }, [user, leads]);

  const leadCreationMessage = useMemo(() => {
    if (user?.status === 'blocked') {
      return 'Your account is blocked. Please contact support.';
    }
    if (user?.status === 'pending' && leads.length > 0) {
      return 'Waiting for admin to approve your first lead before you can create more.';
    }
    return null;
  }, [user, leads]);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", "10");
      const url = `${API}/leads?${params.toString()}`;
      const res = await axios.get(url, { headers: authHeaders });
      setLeads(res.data?.data || []);
      setTotalPages(res.data?.totalPages || 1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load leads");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, page]);

  const lookupCustomerByPhone = useCallback(async (phone) => {
    try {
      setLookupLoading(true);
      const digits = (phone || '').replace(/\D/g, '');
      if (digits.length !== 10) return;
      const res = await axios.get(`${API}/customers/lookup`, { params: { phone: digits }, headers: authHeaders });
      const info = res.data?.data;
      if (info) {
        setCustomer((prev) => ({ ...prev, ...info, phone_number: digits }));
        // Set segment from customer data if available
        if (info.customer_segment) {
          setSegment(info.customer_segment);
        }
      }
    } catch (_e) {
    } finally {
      setLookupLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    const digits = (customer.phone_number || '').replace(/\D/g, '');
    if (digits.length === 10) {
      lookupCustomerByPhone(digits);
    }
  }, [customer.phone_number]);

  const fieldErrors = useMemo(() => {
    const c = customer;
    const phoneDigits = (c.phone_number || '').replace(/\D/g, '');
    const errors = {};

    if (!c.name) errors.name = 'Name is required';
    if (phoneDigits.length !== 10) errors.phone_number = 'Phone must be 10 digits';
    if (!c.email || !EMAIL_REGEX.test(c.email)) errors.email = 'Valid email is required';
    if (!c.delivery_address) errors.delivery_address = 'Address is required';
    if (!c.area_code) errors.area_code = 'Area code is required';
    if (!c.city) errors.city = 'City is required';
    if (!c.district) errors.district = 'District is required';
    if (!c.state) errors.state = 'State is required';

    if (!deliveryDate) errors.delivery_date = 'Delivery date is required';
    else if (deliveryDate < today) errors.delivery_date = 'Delivery date cannot be in the past';

    const validProducts = products.filter(p => p.product_id && p.size_variant && parseFloat(p.price) > 0 && parseInt(p.quantity, 10) >= 1);
    if (validProducts.length === 0) errors.products = 'At least one product with size selected is required';

    return errors;
  }, [customer, products, deliveryDate, today]);

  const isValid = Object.keys(fieldErrors).length === 0;

  const shouldShowError = (fieldName) => {
    return fieldErrors[fieldName] && (touchedFields[fieldName] || attemptedSubmit);
  };

  const handleBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const res = await axios.get(`${API}/products`, { headers: authHeaders });
      setAvailableProducts(res.data?.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load products");
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const addProductRow = () => setProducts((p) => [...p, { product_id: "", product_name: "", size_variant: "", price: 0, quantity: 1 }]);

  const updateProduct = (idx, key, value) => {
    setProducts((ps) => {
      const newProducts = ps.map((p, i) => {
        if (i === idx) {
          const updatedProduct = { ...p, [key]: value };

          if (key === 'product_id' && value) {
            const selectedProduct = availableProducts.find(prod => prod.id === value);
            if (selectedProduct) {
              updatedProduct.product_name = selectedProduct.name;
              updatedProduct.size_variant = "";
              updatedProduct.price = 0;
            }
          }

          if (key === 'size_variant' && value) {
            const selectedProduct = availableProducts.find(prod => prod.id === p.product_id);
            if (selectedProduct && selectedProduct.size_prices && selectedProduct.size_prices[value]) {
              updatedProduct.price = selectedProduct.size_prices[value];
            }
          }

          return updatedProduct;
        }
        return p;
      });
      return newProducts;
    });
  };

  const totalAll = products.reduce((a, p) => a + parseFloat(p.price || 0) * parseInt(p.quantity || 0, 10), 0).toFixed(2);

  const submitLead = async () => {
    setAttemptedSubmit(true);

    if (!isValid) {
      setErr("Please fix the errors below before submitting");
      return;
    }

    try {
      setErr("");
      setSubmitting(true);

      const validProducts = products
        .filter(p => p.product_id && p.size_variant && parseFloat(p.price) > 0)
        .map(p => ({
          product_id: p.product_id,
          size_variant: p.size_variant,
          price: parseFloat(p.price),
          quantity: parseInt(p.quantity, 10)
        }));

      const payload = { customer: { ...customer, customer_segment: segment }, products: validProducts, delivery_date: deliveryDate };
      await axios.post(`${API}/leads`, payload, { headers: authHeaders });

      toast.success("Lead submitted successfully!");
      setOpen(false);
      setShowConfirmDialog(false);
      setSegment("");
      setCustomer({ name: "", phone_number: "", email: "", delivery_address: "", area_code: "", city: "", district: "", state: "" });
      setProducts([{ product_id: "", product_name: "", size_variant: "", price: 0, quantity: 1 }]);
      setDeliveryDate(today);
      setTouchedFields({});
      setAttemptedSubmit(false);
      setPage(1);
      await loadLeads();
    } catch (e) {
      const errorMessage = e?.response?.data?.error || "Failed to submit lead";
      setErr(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = useCallback((lead) => {
    setSelectedLead(lead);
    setShowLeadDetails(true);
  }, []);

  return (
    <div className="page">
      <Toast />
      <LeadDetailsModal
        lead={selectedLead}
        isOpen={showLeadDetails}
        onClose={() => setShowLeadDetails(false)}
      />
      <Toolbar
        loading={loading}
        onRefresh={loadLeads}
        canCreateLead={canCreateLead}
        onAddLead={() => setOpen(true)}
        leadCreationMessage={leadCreationMessage}
      />
      <LeadsTable
        loading={loading}
        leads={leads}
        onRowClick={handleRowClick}
      />
      {!loading && leads?.length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          loading={loading}
        />
      )}
      <AddLeadModal
        open={open}
        onClose={() => setOpen(false)}
        customer={customer}
        setCustomer={setCustomer}
        segment={segment}
        setSegment={setSegment}
        deliveryDate={deliveryDate}
        setDeliveryDate={setDeliveryDate}
        today={today}
        lookupLoading={lookupLoading}
        shouldShowError={shouldShowError}
        fieldErrors={fieldErrors}
        handleBlur={handleBlur}
        loadingProducts={loadingProducts}
        products={products}
        availableProducts={availableProducts}
        updateProduct={updateProduct}
        setProducts={setProducts}
        addProductRow={addProductRow}
        totalAll={totalAll}
        err={err}
        showConfirmDialog={showConfirmDialog}
        setShowConfirmDialog={setShowConfirmDialog}
        submitting={submitting}
        submitLead={submitLead}
      />
    </div>
  );
}


